import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Warehouse, Package, ArrowLeftRight, PackageX } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { StockCurrentTab } from "@/components/admin/inventory/StockCurrentTab";
import { MovementsTab } from "@/components/admin/inventory/MovementsTab";
import { OutOfStockTab } from "@/components/admin/inventory/OutOfStockTab";

// ---------------------------------------------------------------------------
// Tab types
// ---------------------------------------------------------------------------

type TabId = "stock" | "movements" | "out_of_stock";

interface Tab {
  readonly id: TabId;
  readonly label: string;
  readonly icon: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Inventory() {
  const { t } = useTranslation("admin");
  const [activeTab, setActiveTab] = useState<TabId>("stock");

  const tabs: readonly Tab[] = [
    {
      id: "stock",
      label: t("inventory.tab_stock", "Stock actuel"),
      icon: <Package className="h-4 w-4" />,
    },
    {
      id: "movements",
      label: t("inventory.tab_movements", "Mouvements"),
      icon: <ArrowLeftRight className="h-4 w-4" />,
    },
    {
      id: "out_of_stock",
      label: t("inventory.tab_out_of_stock", "Ruptures de stock"),
      icon: <PackageX className="h-4 w-4" />,
    },
  ];

  const handleTabChange = useCallback((id: TabId) => {
    setActiveTab(id);
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Warehouse className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              {t("inventory.title", "Inventaire")}
            </h1>
            <p className="text-sm text-stone-500">
              {t(
                "inventory.subtitle",
                "Suivez le stock et les mouvements de vos produits",
              )}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label={t("inventory.tabs_label", "Onglets inventaire")}
          className="flex gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={[
                "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700",
              ].join(" ")}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab panels */}
        <div role="tabpanel">
          {activeTab === "stock" && <StockCurrentTab />}
          {activeTab === "movements" && <MovementsTab />}
          {activeTab === "out_of_stock" && <OutOfStockTab />}
        </div>
      </div>
    </AdminLayout>
  );
}
